import React, { useState, useCallback, useMemo } from 'react';
import DataGrid from '../../common/DataGrid';
import ViewToolbar from '../../common/ViewToolbar';
import { usePeriodFilter } from '../../../hooks/usePeriodFilter';
import {
  fetchSalesByCustomerSummary,
  fetchSalesByCustomerDetail,
} from '../../../services/reportService';

const currencyFormatter = (params) => {
  if (params.value == null) return '';
  return Number(params.value).toFixed(2);
};

const percentFormatter = (params) => {
  if (params.value == null) return '';
  return `${Number(params.value).toFixed(2)}%`;
};

const dateFormatter = (params) => {
  if (!params.value) return '';
  return new Date(params.value).toLocaleDateString();
};

const StatusRenderer = (params) => {
  const { is_closed } = params.data;
  const status = is_closed ? 'Closed' : 'Open';
  const colorClass = is_closed
    ? 'text-emerald-600 bg-emerald-50'
    : 'text-amber-600 bg-amber-50';

  return (
    <div className="flex items-center h-full">
      <span className={`px-2 py-0.5 rounded-xl text-xs font-semibold ${colorClass}`}>
        {status}
      </span>
    </div>
  );
};

const summaryColumns = [
  { field: 'customer_id', headerName: 'ID', flex: 1, minWidth: 100 },
  { field: 'customer', headerName: 'Customer', flex: 1, minWidth: 150 },
  { field: 'total_revenue', headerName: 'Revenue', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 120, flex: 1 },
  { field: 'total_cost', headerName: 'Cost', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 120, flex: 1 },
  { field: 'total_profit', headerName: 'Profit', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 120, flex: 1 },
  { field: 'gp_percent', headerName: 'GP %', valueFormatter: percentFormatter, type: 'rightAligned', minWidth: 120, flex: 1 },
  { field: 'total_paid', headerName: 'Paid', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 120, flex: 1 },
];

const detailColumns = [
  { field: 'invoice_date', headerName: 'Date', minWidth: 110, flex: 1, valueFormatter: dateFormatter },
  { field: 'invoice_number', headerName: 'No.', minWidth: 80, flex: 0.7 },
  { field: 'total_revenue', headerName: 'Revenue', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 120, flex: 1 },
  { field: 'total_cost', headerName: 'Cost', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 110, flex: 1 },
  { field: 'gp_amount', headerName: 'GP Amount', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 110, flex: 1 },
  { field: 'gp', headerName: 'GP %', valueFormatter: percentFormatter, type: 'rightAligned', minWidth: 80, flex: 0.7 },
  { field: 'amount_paid', headerName: 'Paid', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 110, flex: 1 },
  { headerName: 'Status', cellRenderer: StatusRenderer, minWidth: 90, flex: 0.7 },
];

export default function SalesByCustomerView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gridApi, setGridApi] = useState(null);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailRows, setDetailRows] = useState([]);

  const periodFilter = usePeriodFilter('this_month');

  const loadSummary = useCallback(async () => {
    const { dateFrom, dateTo } = periodFilter;
    if (!dateFrom || !dateTo) return;

    try {
      setLoading(true);
      setSelectedCustomer(null);
      setDetailRows([]);
      const data = await fetchSalesByCustomerSummary(dateFrom, dateTo);
      setRows(data);
    } catch (err) {
      console.error('Error fetching sales by customer:', err);
    } finally {
      setLoading(false);
    }
  }, [periodFilter]);

  const handleCustomerClick = useCallback(async (row) => {
    const { dateFrom, dateTo } = periodFilter;
    if (!dateFrom || !dateTo) return;

    try {
      setLoading(true);
      setSelectedCustomer(row);
      const data = await fetchSalesByCustomerDetail(dateFrom, dateTo, row.customer_id);
      setDetailRows(data);
    } catch (err) {
      console.error('Error fetching customer detail:', err);
    } finally {
      setLoading(false);
    }
  }, [periodFilter]);

  const handleBack = useCallback(() => {
    setSelectedCustomer(null);
    setDetailRows([]);
  }, []);

  const summaryTotals = useMemo(() => {
    if (!rows.length) return null;
    const totalRevenue = rows.reduce((s, r) => s + Number(r.total_revenue || 0), 0);
    const totalCost = rows.reduce((s, r) => s + Number(r.total_cost || 0), 0);
    const totalProfit = totalRevenue - totalCost;
    const totalPaid = rows.reduce((s, r) => s + Number(r.total_paid || 0), 0);
    const gpPercent = totalRevenue === 0 ? 0 : ((totalProfit / totalRevenue) * 100);
    return { totalRevenue, totalCost, totalProfit, totalPaid, gpPercent };
  }, [rows]);

  const detailTotals = useMemo(() => {
    if (!detailRows.length) return null;
    const totalRevenue = detailRows.reduce((s, r) => s + Number(r.total_revenue || 0), 0);
    const totalCost = detailRows.reduce((s, r) => s + Number(r.total_cost || 0), 0);
    const gpAmount = totalRevenue - totalCost;
    const totalPaid = detailRows.reduce((s, r) => s + Number(r.amount_paid || 0), 0);
    const gp = totalRevenue === 0 ? 0 : ((gpAmount / totalRevenue) * 100);
    return { totalRevenue, totalCost, gpAmount, totalPaid, gp };
  }, [detailRows]);

  const activeTotals = selectedCustomer ? detailTotals : summaryTotals;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar row */}
      <div className="mb-4 flex flex-wrap gap-3 items-end">
        <ViewToolbar
          searchTerm=""
          onSearchChange={() => {}}
          placeholder="Search..."
          addLabel="Run Report"
          addColor="bg-blue-600 hover:bg-blue-700"
          onAddNew={loadSummary}
          onExportCsv={gridApi ? () => gridApi.exportToCsv() : undefined}
          period={periodFilter.period}
          onPeriodChange={periodFilter.onPeriodChange}
          dateFrom={periodFilter.dateFrom}
          onDateFromChange={periodFilter.onDateFromChange}
          dateTo={periodFilter.dateTo}
          onDateToChange={periodFilter.onDateToChange}
        />
      </div>

      {/* Breadcrumb / back navigation when drilling into a customer */}
      {selectedCustomer && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <button
            onClick={handleBack}
            className="text-blue-600 hover:text-blue-800 font-medium cursor-pointer flex items-center gap-1"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            All Customers
          </button>
          <span className="text-gray-400">/</span>
          <span className="font-semibold text-gray-700">{selectedCustomer.customer}</span>
        </div>
      )}

      {/* Totals bar */}
      {activeTotals && (
        <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <TotalCard label="Revenue" value={activeTotals.totalRevenue} />
          <TotalCard label="Cost" value={activeTotals.totalCost} />
          <TotalCard label="Profit" value={selectedCustomer ? activeTotals.gpAmount : activeTotals.totalProfit} />
          <TotalCard
            label="GP %"
            value={selectedCustomer ? activeTotals.gp : activeTotals.gpPercent}
            suffix="%"
            isCurrency={false}
          />
          <TotalCard label="Paid" value={activeTotals.totalPaid} />
        </div>
      )}

      {/* Loading / empty / grid */}
      {loading ? (
        <div className="flex justify-center mt-10 text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="flex justify-center mt-10 text-gray-400 text-sm">
          Select a period and click "Run Report" to view sales data.
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          {selectedCustomer ? (
            <DataGrid
              rowData={detailRows}
              columns={detailColumns}
              onGridReady={setGridApi}
              exportFileName="sales-by-customer-detail.csv"
              pagination
              paginationSize={20}
            />
          ) : (
            <DataGrid
              rowData={rows}
              columns={summaryColumns}
              onEdit={handleCustomerClick}
              onGridReady={setGridApi}
              exportFileName="sales-by-customer.csv"
              editLabel="View"
              pagination
              paginationSize={20}
            />
          )}
        </div>
      )}
    </div>
  );
}

function TotalCard({ label, value, suffix, isCurrency = true }) {
  const formatted = isCurrency
    ? Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : Number(value || 0).toFixed(2);

  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm">
      <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-lg font-semibold text-gray-800 mt-0.5">
        {isCurrency && '$'}{formatted}{suffix || ''}
      </div>
    </div>
  );
}
